/**
 * Swipe-to-dismiss for offcanvas panels.
 *
 * Direction is read from [data-swipe-dismiss]:
 *   "left"  – swipe left to close  (default for .offcanvas-start)
 *   "down"  – pull down to close   (for filter sidebars, etc.)
 *
 * For "down" panels both show and hide are overridden:
 *   show  → slides up from the bottom
 *   hide  → slides down (button, backdrop, escape, swipe — all consistent)
 *
 * Down-swipe only activates when the .offcanvas-body is scrolled to the top,
 * so normal vertical scrolling inside the panel is preserved.
 */

export default (() => {
  const THRESHOLD = 80;
  const LOCK_PX = 10;
  const DOWN_ANIM_MS = 300;

  document.querySelectorAll(".offcanvas-start").forEach((el) => {
    const dir = el.dataset.swipeDismiss || "left";
    const isDown = dir === "down";

    let startX, startY, currentX, currentY;
    let tracking, decided, isSwipe;

    if (isDown) {
      let finishing = false;
      let showCleanup = null;

      // --- SHOW: slide up from bottom instead of left ---

      el.addEventListener("show.bs.offcanvas", () => {
        if (showCleanup) {
          clearTimeout(showCleanup);
          showCleanup = null;
        }
        el.style.transition = "none";
        el.style.transform = "translateY(100vh)";
        el.style.opacity = "0";
      });

      el.addEventListener("shown.bs.offcanvas", () => {
        void el.offsetHeight;
        el.style.transition = `transform ${DOWN_ANIM_MS}ms ease-out, opacity ${DOWN_ANIM_MS}ms ease-out`;
        el.style.transform = "translateY(0)";
        el.style.opacity = "1";

        showCleanup = setTimeout(() => {
          showCleanup = null;
          el.style.transition = "";
          el.style.transform = "";
          el.style.opacity = "";
        }, DOWN_ANIM_MS);
      });

      // --- HIDE: slide down instead of left ---

      el.addEventListener("hide.bs.offcanvas", (e) => {
        if (finishing) return;

        if (showCleanup) {
          clearTimeout(showCleanup);
          showCleanup = null;
        }

        e.preventDefault();

        el.style.transition = `transform ${DOWN_ANIM_MS}ms ease-in, opacity ${DOWN_ANIM_MS}ms ease-in`;
        void el.offsetHeight;
        el.style.transform = "translateY(100vh)";
        el.style.opacity = "0";

        setTimeout(() => {
          el.style.transition = "none";
          el.style.transform = "";
          el.style.opacity = "";

          finishing = true;
          /* eslint-disable no-undef */
          const inst = bootstrap.Offcanvas.getInstance(el);
          /* eslint-enable no-undef */
          if (inst) inst.hide();

          requestAnimationFrame(() => {
            finishing = false;
            el.style.transition = "";
          });
        }, DOWN_ANIM_MS);
      });
    }

    // --- Touch / swipe tracking ---

    el.addEventListener(
      "touchstart",
      (e) => {
        if (!el.classList.contains("show")) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        currentX = startX;
        currentY = startY;
        tracking = true;
        decided = false;
        isSwipe = false;
      },
      { passive: true },
    );

    el.addEventListener(
      "touchmove",
      (e) => {
        if (!tracking) return;

        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;

        if (!decided) {
          const dx = Math.abs(x - startX);
          const dy = Math.abs(y - startY);

          if (dx > LOCK_PX || dy > LOCK_PX) {
            decided = true;

            if (isDown) {
              const body = el.querySelector(".offcanvas-body");
              const atTop = !body || body.scrollTop <= 0;
              isSwipe = dy > dx && y - startY > 0 && atTop;
            } else {
              isSwipe = dx > dy;
            }

            if (isSwipe) {
              el.style.transition = "none";
            }
          }
          return;
        }

        if (!isSwipe) return;

        if (isDown) {
          currentY = y;
          const diff = currentY - startY;
          if (diff > 0) {
            el.style.transform = `translateY(${diff}px)`;
            el.style.opacity = Math.max(1 - diff / 400, 0.2);
          }
        } else {
          currentX = x;
          const diff = currentX - startX;
          if (diff < 0) {
            el.style.transform = `translateX(${diff}px)`;
          }
        }
      },
      { passive: true },
    );

    el.addEventListener("touchend", () => {
      if (!tracking) return;
      tracking = false;

      if (!isSwipe) {
        el.style.transition = "";
        el.style.transform = "";
        el.style.opacity = "";
        return;
      }

      const diff = isDown ? currentY - startY : startX - currentX;

      if (diff <= THRESHOLD) {
        el.style.transition = "";
        el.style.transform = "";
        el.style.opacity = "";
        return;
      }

      /* eslint-disable no-undef */
      const instance = bootstrap.Offcanvas.getInstance(el);
      /* eslint-enable no-undef */
      if (!instance) return;

      if (isDown) {
        instance.hide();
      } else {
        el.style.transition = "";
        el.style.transform = "";
        instance.hide();
      }
    });
  });
})();
